import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DriverManagementService } from '@app-driver-management/services/driver-management.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { ValidatorService } from '@app-shared/services/validator/validator.service';
import { DRIVERID_INPUT_PATTERN } from '@app-driver-management/common/driver-management.constants';
import { ActivatedRoute } from '@angular/router';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
@Component({
  selector: 'app-edit-installer',
  templateUrl: './edit-installer.component.html',
  styleUrls: ['./edit-installer.component.scss'],
})
export class EditInstallerComponent implements OnInit {
  public form: FormGroup;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public errorMessage: string;
  public disableSubmitButton: boolean = true;
  public loader: boolean = false;
  public mode: string;
  public data;
  constructor(
    private location: Location,
    private driverManagementService: DriverManagementService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private validatorService: ValidatorService,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.data = params;
      this.mode = params['mode'];
      const installerId = params['installerId'];
      const installerName = params['installerName'];
      const email = params['email'];
      this.form = this.fb.group({
        installerId: [installerId || '', [Validators.required, Validators.maxLength(48), Validators.pattern(DRIVERID_INPUT_PATTERN)]],
        installerName: [installerName || '', [Validators.required, Validators.pattern(`^[a-zA-Z]+(?: [a-zA-Z]+){0,31}$`)]],
        email: [email || '', [Validators.required, this.validatorService.customEmailValidator()]],
      });
      if (this.mode === 'edit') {
        this.form.get('installerId').disable();
        this.form.get('email').disable();
      }
    });

    this.form.valueChanges.subscribe(() => {
      this.disableSubmitButton = this.form.invalid;
    });
  }
  public ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }
  public onSubmit() {
    if (this.mode == 'add') {
      this.addInstallerDetails();
      this.mixpanelService.track('Save Details - Add Installer / Installer Page');
    } else {
      this.editInstallerDetails();
      this.mixpanelService.track('Save Details - Edit Installer / Installer Page');
    }
  }
  public editInstallerDetails() {
    const { email, installerId } = this.data;
    const updateInstallerBody = {
      username: email?.length > 0 ? email : installerId,
      personId: '',
      driverId: installerId,
      email: email,
      driverName: this.form.get('installerName').value,
    };
    return this.driverManagementService
      .updateDriverDetails(updateInstallerBody)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.loader = false;
          this.snackBarService.success(this.translate.instant('InstallerEditSuccessfully'));
          this.navigateBack();
        },
        (err) => {
          this.loader = false;
          if (err === 'No internet') {
            // Handle no internet case
            this.errorMessage = this.translate.instant('NetworkIssue');
            return;
          }
          this.errorMessage = this.translate.instant('InstallerEditFailed');
        }
      );
  }
  public addInstallerDetails(): void {
    this.loader = true;
    const params = {
      driverId: this.form.get('installerId').value,
      driverName: this.form.get('installerName').value,
      email: this.form.get('email').value,
      userType: 'INSTALLER',
    };
    this.driverManagementService
      .driverSignup(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.loader = false;
          this.snackBarService.success(this.translate.instant('driverManagementInstallerSignUpComplete'));
          this.navigateBack();
        },
        (err) => {
          this.loader = false;
          if (err === 'No internet') {
            // Handle no internet case
            this.errorMessage = this.translate.instant('NetworkIssue');
            return;
          }
          if (err.status == 409 && err.error.errCode === 'DRIVER_ID_EXIST') {
            this.errorMessage = this.translate.instant('InstallerIdExists');
          } else if (err.status == 409 && err.error.errCode === 'EMAIL_ID_EXIST') {
            this.errorMessage = this.translate.instant('InstallerEmailExists');
          } else if (err.status == 409 && err.error.errCode === 'INSTALLER_EXIST_AS_DRIVER') {
            this.errorMessage = this.translate.instant('installerExistAsDriver');
          } else {
            this.errorMessage = this.translate.instant('driverManagementInstallerSignUpFailed');
          }
        }
      );
  }
  public navigateBack(): void {
    this.location.back();
  }
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
